import { selectTemplates } from '@src/data/business/Templates/selectors'
import { IPostShiftFormValues } from '@src/features/shift-post-v2/pages/post-shift/post-shift.form'
import { URLManager } from '@src/helpers/URLManager'
import { ITemplate } from '@network/models/template.model'
import { FormikProps } from 'formik'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { IBusinessEntity } from '@network/models/business-entity.model'

/**
 * Hook to handle the template selection and update the formik values
 */
export const useTemplate = (
  formik: FormikProps<IPostShiftFormValues>,
  isTemplateSection?: boolean,
  businessEntity?: IBusinessEntity,
) => {
  const navigate = useNavigate()
  const { setFieldValue } = formik
  const templates = useSelector(selectTemplates)
  const isAutoAcceptEnabled = businessEntity?.auto_accept
  /**
   * Updates the formik values with the selected template data
   */
  const updateFormikWithTemplateData = useCallback(
    (selectedTemplate: ITemplate) => {
      setFieldValue('template_id', selectedTemplate.id)
      setFieldValue('gig_type', selectedTemplate.gig_type?.name ?? '')
      setFieldValue('gig_type_id', selectedTemplate.gig_type_id)
      setFieldValue('description_short', selectedTemplate.description_short)
      setFieldValue(
        'description_rich_text',
        selectedTemplate.description_rich_text,
      )
      setFieldValue(
        'special_instructions_rich_text',
        selectedTemplate.special_instructions_rich_text,
      )
      setFieldValue(
        'what_to_wear_rich_text',
        selectedTemplate.what_to_wear_rich_text,
      )
      setFieldValue(
        'travel_tips_rich_text',
        selectedTemplate.travel_tips_rich_text,
      )
      setFieldValue('training_mode', selectedTemplate.training_mode)
      setFieldValue('min_age', selectedTemplate.min_age)
      setFieldValue(
        'amount_lifted_frequently',
        selectedTemplate.amount_lifted_frequently,
      )
      // If the frequently lifted weight is 50, set the occasionally lifted weight to 50
      const maxLiftingWeight = 50
      const isLiftedFrequentlyMax =
        selectedTemplate.amount_lifted_frequently === maxLiftingWeight

      const amountLiftedOccasionally = isLiftedFrequentlyMax
        ? maxLiftingWeight
        : selectedTemplate.amount_lifted_occasionally
      setFieldValue('amount_lifted_occasionally', amountLiftedOccasionally)
      setFieldValue(
        'amount_of_time_spent_on_feet',
        selectedTemplate.amount_of_time_spent_on_feet,
      )
      //set auto accept default value
      if (isAutoAcceptEnabled) {
        setFieldValue('auto_accept', true)
      }

      if (selectedTemplate.auto_accept_certificates) {
        const ids = selectedTemplate.auto_accept_certificates.data.map(
          certificate => certificate.id,
        )
        setFieldValue('auto_accept_certificate_ids', ids)
      }
    },
    [setFieldValue, isAutoAcceptEnabled],
  )

  useEffect(() => {
    // Disable this effect for the template section
    if (!isTemplateSection) {
      const urlManager = new URLManager(navigate)
      const templateIdParam = urlManager.getParam('template_id')

      if (
        templateIdParam &&
        templates.some(t => t.id === Number(templateIdParam))
      ) {
        const template = templates.find(t => t.id === Number(templateIdParam))
        if (template) {
          updateFormikWithTemplateData(template)
        }
      }
    }
  }, [isTemplateSection, navigate, templates, updateFormikWithTemplateData])

  return {
    updateFormikWithTemplateData,
  }
}
